import store from 'store';

import { api } from '../api';

type UpdateProfileDraftStateParams = {
  profileId: number;
  rivalId: number;
  isDraft: boolean;
};

type UpdateProfileDraftStateResponse = {
  isDraft: boolean;
};

export type UpdateProfileSettingsParams = {
  profileId: number;
  isDraft?: boolean;
  isTemplate?: boolean;
  name?: string;
  imageUrl?: string;
  curators?: {
    remove?: number[];
    add?: number[];
  };
  wideMode?: boolean;
  publicSourcesEnabled?: boolean;
  products?: number[];
};

type UpdateProfileSettingsResponse = {
  id: number;
  isDraft: boolean;
  isTemplate: boolean;
  name: string;
  curators: number[];
  updatedAt: Date;
  rivalId: number;
  publicSourcesEnabled: boolean;
};

type CloneProfileResponse = {
  cardsCount: number;
  battlecardsCount: number;
};

type CloneProfileParams = {
  profileId: number;
  fromId: number;
  rivalId: number;
};

const extendedApi = api.injectEndpoints({
  endpoints: (build) => ({
    updateProfileDraftState: build.mutation<
      UpdateProfileDraftStateResponse,
      UpdateProfileDraftStateParams
    >({
      query: ({ profileId, rivalId, ...body }) => ({
        url: `profiles/${profileId}`,
        method: 'PUT',
        body,
      }),
      onQueryStarted: async ({ rivalId, isDraft }, { queryFulfilled }) => {
        try {
          await queryFulfilled;
          store.dispatch.rivals.updateRivalById({
            id: rivalId.toString(),
            changes: { isDraft },
          });
        } catch {
          // Do nothing
        }
      },
    }),
    updateProfileSettings: build.mutation<
      UpdateProfileSettingsResponse,
      UpdateProfileSettingsParams
    >({
      query: ({ profileId, ...body }) => ({
        url: `profiles/${profileId}`,
        method: 'PUT',
        body,
      }),
      onQueryStarted: async ({ imageUrl }, { queryFulfilled }) => {
        try {
          const {
            data: {
              id: profileId,
              isDraft,
              isTemplate,
              name,
              rivalId,
              publicSourcesEnabled,
            },
          } = await queryFulfilled;

          await store.dispatch.profiles.fetchProfileById(profileId);

          store.dispatch.rivals.updateRivalById({
            id: rivalId.toString(),
            changes: {
              ...(typeof isDraft === 'boolean' && { isDraft }),
              ...(typeof isTemplate === 'boolean' && { isTemplate }),
              ...(name && { name }),
              ...(imageUrl && { iconUrl: imageUrl }),
              ...(typeof publicSourcesEnabled === 'boolean' && {
                publicSourcesEnabled,
              }),
            },
          });
        } catch {
          // Do nothing
        }
      },
    }),
    cloneProfile: build.mutation<CloneProfileResponse, CloneProfileParams>({
      query: ({ profileId: id, rivalId, ...rest }) => ({
        url: `profiles/${id}.json`,
        method: 'POST',
        body: { id, ...rest },
      }),
      onQueryStarted: async ({ profileId, rivalId }, { queryFulfilled }) => {
        try {
          const {
            data: { cardsCount, battlecardsCount },
          } = await queryFulfilled;
          await store.dispatch.profiles.fetchProfileById(profileId);
          store.dispatch.rivals.updateRivalById({
            id: rivalId.toString(),
            changes: {
              cardsCount,
              battlecardsCount,
            },
          });
        } catch {
          // Do nothing
        }
      },
    }),
  }),
});

export const {
  useUpdateProfileDraftStateMutation,
  useUpdateProfileSettingsMutation,
  useCloneProfileMutation,
} = extendedApi;
